// Nine Lives '84 - high score table.
//
// Ten entries kept in localStorage. The storage backend is injectable so the
// logic can be tested without a browser.

export interface HighScore {
  score: number;
  level: number;
  mode: string;
  date: string;
  medals: number;
}

export interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

const KEY = 'ninelives84.highscores';
/** Where scores lived before the game was renamed; read once and copied over. */
const LEGACY_KEY = 'alleycat.highscores';
/** The top difficulty's old name, as stored in `mode` by the old build. */
const LEGACY_MODES: Record<string, string> = { 'Alley Cat': 'Stray' };
const MAX = 10;

export class HighScores {
  private entries: HighScore[] = [];

  constructor(private readonly store: KeyValueStore | null) {
    try {
      let raw = store?.getItem(KEY);
      const legacy = !raw;
      if (legacy) raw = store?.getItem(LEGACY_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as unknown;
        if (Array.isArray(parsed)) {
          this.entries = parsed
            .filter(
              (e): e is HighScore =>
                typeof e === 'object' && e !== null && typeof e.score === 'number',
            )
            .map((e) => ({ ...e, mode: LEGACY_MODES[e.mode] ?? e.mode }));
        }
        if (legacy && this.entries.length > 0) this.save();
      }
    } catch {
      this.entries = [];
    }
  }

  get list(): readonly HighScore[] {
    return this.entries;
  }

  /** Insert a score; returns its 1-based rank, or 0 if it did not make the table. */
  add(entry: HighScore): number {
    if (entry.score <= 0) return 0;
    const list = [...this.entries, entry].sort((a, b) => b.score - a.score).slice(0, MAX);
    const rank = list.indexOf(entry) + 1;
    if (rank === 0) return 0;
    this.entries = list;
    this.save();
    return rank;
  }

  private save(): void {
    try {
      this.store?.setItem(KEY, JSON.stringify(this.entries));
    } catch {
      // Storage may be unavailable (private mode); keep the in-memory copy.
    }
  }

  /** Would this score make the table? */
  qualifies(score: number): boolean {
    if (score <= 0) return false;
    return this.entries.length < MAX || score > this.entries[this.entries.length - 1].score;
  }
}
